import { useDidUpdate } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { StatusLabelsProvider } from '../../../contexts/status-labels.context';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import {
  useFlowByPhaseTabStore,
  useStrategyStore,
  useThroughputTabStore,
} from '../../../store/strategy-store/strategy-store';
import {
  resetInitiativePerformanceState,
  setActivePanel,
  setFlowByPhaseSelectedEpic,
  setInitiatives,
  setKPIOverviewData,
  setPerformanceActivePanel,
  setSelectedInitiative,
} from '../../../store/strategy-store/strategy-store.actions';
import { Panel } from '../../../store/strategy-store/strategy-store.type';
import { useFlora } from '../../flora/flora.context';
import { FloraSupportedObjectTypes } from '../../flora/flora.type';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { toast } from '../../notifications';
import { GeneralViewNames } from '../../side-bar/side-bar.type';
import { InitiativeModal } from '../initiative-modal/initiative-modal';
import { InitiativeModalMode } from '../initiative-modal/initiative-modal.type';
import { useFlowByPhaseData } from './flow-by-phase-tab/flow-by-phase-tab.hooks';
import { InitiativePerformanceControlBar } from './initiative-performance-control-bar';
import { InitiativePerformanceFilters } from './initiative-performance-filters';
import { InitiativePerformanceHeader } from './initiative-performance-header';
import { InitiativePerformanceMainSection } from './initiative-performance-main-section';
import { InitiativePerformanceModal } from './initiative-performance-modal';
import { InitiativePerformanceOverview } from './initiative-performance-overview';
import { InitiativePerformanceSideSection } from './initiative-performance-side-section';
import { InitiativePerformanceTabs } from './initiative-performance-tabs';
import {
  useContributingBoards,
  useDeleteInitiative,
  useEditInitiative,
  useInitiativeFocusData,
  useInitiativePerformanceData,
  useSideSection,
} from './initiative-performance.hooks';
import { PerformanceTab, ThroughputMode } from './initiative-performance.type';
import { useTargetsData } from './targets-tab/targets-tab.hooks';

/**
 * Initiative performance page: overview, tabs, main section, and side panels
 * for the currently selected initiative, goal, or objective.
 *
 * Responsibilities:
 * 1. Load the initiative's performance, focus, flow-by-phase, and targets data.
 * 2. Publish the initiative to Flora as contextual info for the chat scope.
 * 3. Provide source-cased status labels for every board that contributed to the
 *    initiative, so sub-group charts and status filters below render the
 *    customer's own casing.
 * 4. Reset page state on unmount and redirect when the portfolio changes.
 *
 * @returns {JSX.Element} The initiative performance page
 */
export const InitiativePerformance = () => {
  useDocumentTitle('Initiative Performance - Bloomfilter');

  const i18n = useTranslation();
  const navigate = useNavigate();

  const portfolio = useGlobalStore((state) => state.portfolio);
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const performanceTab = useStrategyStore((state) => state.performanceTab);
  const throughputMode = useThroughputTabStore((state) => state.mode);
  const performanceActivePanel = useStrategyStore((state) => state.performanceActivePanel);
  // Must match the argument the Flow-by-Phase tab passes: both instances write the same
  // store slot, and the page-level effect runs last, so a bare call here would overwrite
  // the tab's epic-filtered data with unfiltered initiative totals.
  const flowByPhaseSelectedEpic = useFlowByPhaseTabStore((state) => state.selectedEpic || undefined);
  const { setContextualInfo } = useFlora();

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  useEffect(() => {
    if (initiative) {
      setContextualInfo({
        currentModule: GeneralViewNames.Strategy,
        currentObjects: [{ type: FloraSupportedObjectTypes.Initiative, id: initiative.id, name: initiative.name }],
        startDate: initiative.start_date ?? null,
        endDate: initiative.end_date ?? null,
      });
    }
  }, [initiative, setContextualInfo]);

  const { isLoading } = useInitiativePerformanceData();
  useInitiativeFocusData();
  useFlowByPhaseData(flowByPhaseSelectedEpic);
  useTargetsData();

  const showSideFilters = performanceActivePanel === Panel.Filters;
  const showSideSection = useSideSection();

  const editInitiativeMutation = useEditInitiative({
    portfolioId: portfolio?.id,
    initiativeId: initiative?.id,
    onSuccess: () => {
      setIsEditModalOpen(false);
      setFlowByPhaseSelectedEpic(null);
    },
  });

  const deleteInitiativeMutation = useDeleteInitiative({
    portfolioId: portfolio?.id,
    initiativeId: initiative?.id,
    onSuccess: () => {
      toast.success(i18n.t('strategy.initiatives.deleted_success', { name: initiative?.name }));
      setSelectedInitiative(null);
      setActivePanel(Panel.Filters);
      setIsEditModalOpen(false);
      navigate('/application/strategy', { replace: true });
    },
  });

  useDidUpdate(() => {
    // Only reset and navigate if we're already on the initiative performance page
    // and the portfolio actually changed to a different one
    if (portfolio && initiative && initiative.portfolio !== portfolio.id) {
      setInitiatives([]);
      setKPIOverviewData(null);
      setActivePanel(Panel.Filters);
      navigate('/application/strategy', {
        replace: true,
      });
    }
  }, [portfolio?.id]);

  useEffect(() => {
    return () => {
      resetInitiativePerformanceState();
    };
  }, []);

  const filtersTitle =
    performanceTab === PerformanceTab.Throughput && throughputMode === ThroughputMode.InitiativeProgress
      ? i18n.t('strategy.initiative_performance.details_title')
      : i18n.t('common.filters');

  const setShowSideFilters = (show: boolean) => {
    setPerformanceActivePanel(show ? Panel.Filters : null);
  };

  // Sub-group charts and status filters on this page span every board that
  // contributed to the initiative, so labels are merged across all of them.
  // `useContributingBoards` builds a fresh array each render, so the memo is
  // keyed on the joined ids — otherwise the query list underneath would be
  // rebuilt and re-defaulted on every render of the page.
  const contributingBoards = useContributingBoards();
  const contributingBoardIdKey = contributingBoards.map((board) => board.id).join('|');
  const contributingBoardIds = useMemo(
    () => (contributingBoardIdKey ? contributingBoardIdKey.split('|') : []),
    [contributingBoardIdKey],
  );

  return (
    <MenuDrivenContainer header={<InitiativePerformanceHeader />}>
      <StatusLabelsProvider subprojectIds={contributingBoardIds}>
        <BrandedLoadingOverlay visible={isLoading} variant="colored" transitionDuration={30} />
        <PageLayout
          controlBar={
            <InitiativePerformanceControlBar openInfoModal={setIsInfoModalOpen} openEditModal={setIsEditModalOpen} />
          }
          overview={{
            title: i18n.t('strategy.overview.title'),
            content: <InitiativePerformanceOverview />,
          }}
          tabs={<InitiativePerformanceTabs />}
          sideFilters={{
            title: filtersTitle,
            content: <InitiativePerformanceFilters setShow={setShowSideFilters} />,
            show: showSideFilters,
            setShow: setShowSideFilters,
          }}
          mainSection={<InitiativePerformanceMainSection />}
          sideSection={<InitiativePerformanceSideSection />}
          showSideSection={showSideSection}
          data-page-layout
        />
        <InitiativePerformanceModal opened={isInfoModalOpen} onClose={() => setIsInfoModalOpen(false)} />
        {initiative && (
          <InitiativeModal
            mode={InitiativeModalMode.Edit}
            initiative={initiative}
            opened={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSubmit={editInitiativeMutation.mutateAsync}
            isSubmitting={editInitiativeMutation.isPending}
            onDelete={deleteInitiativeMutation.mutateAsync}
            isDeleting={deleteInitiativeMutation.isPending}
          />
        )}
      </StatusLabelsProvider>
    </MenuDrivenContainer>
  );
};
